import { fontWeightMediumLarge } from 'utils/fontWeightConstants';
import { Box, makeStyles, Typography } from 'vendor/material';
import { SolutionFieldEnum, type SolutionInputType } from '../sectionSchema';
import { Banner, Button } from '@tackle-io/platform-ui';
import { TrashCan } from 'mdi-material-ui';
import { useReferralFormContext } from '../../ReferralFormContext';
import FirstPartyFormFields from '../FirstPartyFormFields';
import { useCallback, useMemo } from 'react';
import useMsftSolutions from '../../../hooks/useMsftSolutions';
import Tag from 'packages/cosell/src/components/Tag/Tag';
import { TagColorEnum } from 'packages/cosell/src/types/enums';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    paddingBlock: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.NEUTRAL030}`,
    display: 'grid',
    gap: theme.spacing(1.2),
  },
  rowContent: {
    color: theme.palette.NEUTRAL900,
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    maxWidth: '100%',
    gap: theme.spacing(2),
  },
  label: {
    fontWeight: fontWeightMediumLarge,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: theme.typography.pxToRem(20),
  },

  name: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: fontWeightMediumLarge,
    lineHeight: theme.typography.pxToRem(21),
  },
  detailLine: {
    display: 'flex',
    gap: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
    lineHeight: theme.typography.pxToRem(20),
    '&>span': {
      fontWeight: fontWeightMediumLarge,
    },
  },
  iconButton: {
    height: 'max-content',
    alignSelf: 'center',
    justifySelf: 'flex-end',
    transform: 'translateY(-5px)',
  },
  flexContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: '300px',
    flexGrow: 1,
  },
  formSection: {
    minWidth: '400px',
    flex: 1,
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  detailsGroup: {
    display: 'grid',
    gap: theme.spacing(0.5),
  },
  incentivesWrap: {
    display: 'flex',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(12),
  },
}));

const InvalidSolutionBanner = () => (
  <Banner
    borderPosition="top"
    title="Invalid solution. Edit the co-sell to remove. You can still add valid solutions."
    type="warning"
  />
);

const incentiveMap = {
  'MSFT Internal Azure IP Co-Sell': 'IP Co-sell',
  'MACC (Customer)': 'MACC',
} as const;

const IncentiveTags = ({ incentives }: { incentives: string[] }) => {
  const getIncentiveLabel = useCallback(
    (incentive: typeof incentives[number]) => {
      return incentive in incentiveMap ? incentiveMap[incentive] : incentive;
    },
    [],
  );

  return (
    <>
      {incentives.map((incentive) => {
        const label = getIncentiveLabel(incentive);
        return (
          <Tag
            color={label === 'MACC' ? TagColorEnum.GREEN : undefined}
            key={incentive}
          >
            {label}
          </Tag>
        );
      })}
    </>
  );
};

const SolutionListItem = ({
  fieldName,
  isInvalid = false,
  onDelete,
  solution,
}: {
  fieldName: string;
  isInvalid?: boolean;
  onDelete: () => void;
  solution: SolutionInputType;
}) => {
  const classes = useStyles();
  const { register } = useReferralFormContext();

  const hiddenFields = (
    <>
      <input
        type="hidden"
        ref={register()}
        name={`${fieldName}.${SolutionFieldEnum.type}`}
        defaultValue={solution.type}
      />
      <input
        type="hidden"
        ref={register()}
        name={`${fieldName}.${SolutionFieldEnum.id}`}
        defaultValue={solution.id}
      />
      <input
        type="hidden"
        ref={register()}
        name={`${fieldName}.${SolutionFieldEnum.name}`}
        defaultValue={solution.name}
      />
      <input
        type="hidden"
        ref={register()}
        name={`${fieldName}.${SolutionFieldEnum.publisherName}`}
        defaultValue={solution.publisherName}
      />
      <input
        type="hidden"
        ref={register()}
        name={`${fieldName}.${SolutionFieldEnum.solutionType}`}
        defaultValue={solution.solutionType}
      />
    </>
  );

  const { data } = useMsftSolutions();

  const solutions = data?.solutions;

  const fullSolutionData = useMemo(() => {
    if (!solutions) return null;
    const solutionData = solutions.find(
      (sol) => sol.solutionId === solution.id,
    );
    return solutionData || null;
  }, [solutions, solution.id]);

  return (
    <Box data-testid="solution-list-item" className={classes.wrapper}>
      {!!isInvalid && <InvalidSolutionBanner />}
      <Box className={classes.rowContent}>
        {hiddenFields}

        <Box className={classes.flexContent}>
          <Box className={classes.details}>
            <Typography variant="caption">Solution</Typography>
            <Box className={classes.detailsGroup}>
              <div className={classes.name}>{solution.name}</div>
              <div>
                <div data-testid="solution-id" className={classes.detailLine}>
                  <span>SolutionId:</span> {solution.id}
                </div>
                <div data-testid="publisher" className={classes.detailLine}>
                  <span>Publisher:</span> {solution.publisherName}
                </div>
              </div>
              <Box display="flex" className={classes.incentivesWrap}>
                {fullSolutionData?.incentives ? (
                  <IncentiveTags incentives={fullSolutionData?.incentives} />
                ) : null}
              </Box>
            </Box>
          </Box>

          <Box className={classes.formSection}>
            {solution.solutionType === '1P' ? (
              <FirstPartyFormFields fieldNamePrefix={fieldName} />
            ) : null}
          </Box>
        </Box>
        <Button
          aria-label="Remove solution"
          className={classes.iconButton}
          variant="text"
          startIcon={<TrashCan />}
          onClick={onDelete}
        />
      </Box>
    </Box>
  );
};

export default SolutionListItem;
